import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API, TOKEN_KEY } from '../../contexts/AuthContext';
import ModalBase from '../ModalBase';
import CameraPlayer from './CameraPlayer';
import CalendarioGravacoes from './CalendarioGravacoes';

// Recorte de gravacao (permissao ti:cameras:recortar).
//
// A gravacao continua SO no DVR. Aqui a pessoa escolhe no calendario um dia e
// um trecho que o DVR tem gravado (CalendarioGravacoes), assiste, marca inicio
// e fim (assistindo ou digitando) e pede o recorte:
// o servidor copia esse trecho do DVR para um MP4 temporario, que fica 24h
// disponivel para baixar.

const POLL_RAPIDO_MS = 3000;
const POLL_LENTO_MS = 20000;

const p2 = (n) => String(n).padStart(2, '0');

// Date -> "2026-10-05T14:30:05" (hora local: e a hora do DVR).
function formatarLocal(d) {
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}T${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}`;
}

// "2026-10-05T14:30" ou com segundos -> Date local (sem passar por fuso).
function lerLocal(texto) {
  const m = String(texto || '').match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return null;
  return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0));
}

function somarSegundos(texto, segundos) {
  const d = lerLocal(texto);
  return d ? formatarLocal(new Date(d.getTime() + segundos * 1000)) : texto;
}

function formatarDuracao(s) {
  if (!Number.isFinite(s) || s <= 0) return '—';
  const m = Math.floor(s / 60);
  return m ? `${m} min ${p2(Math.round(s % 60))} s` : `${Math.round(s)} s`;
}

function formatarPeriodo(inicio, fim) {
  const a = new Date(inicio);
  const b = new Date(fim);
  const data = `${p2(a.getDate())}/${p2(a.getMonth() + 1)}/${a.getFullYear()}`;
  const hora = (d) => `${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}`;
  return `${data} ${hora(a)} – ${hora(b)}`;
}

function formatarTamanho(bytes) {
  if (!bytes) return '';
  return bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;
}

export default function RecortesModal({ catalogo, cameraInicial, onClose }) {
  const gravadoresPorId = useMemo(() => new Map(catalogo.gravadores.map((g) => [g.id, g])), [catalogo.gravadores]);
  const camerasPorGravador = useMemo(() => [...catalogo.gravadores]
    .sort((a, b) => a.ordem - b.ordem || String(a.nome).localeCompare(String(b.nome), 'pt-BR'))
    .map((g) => ({
      gravador: g,
      cameras: catalogo.cameras.filter((c) => c.gravador_id === g.id).sort((a, b) => a.ordem - b.ordem || a.canal - b.canal),
    }))
    .filter((s) => s.cameras.length), [catalogo]);

  const [cameraId, setCameraId] = useState(cameraInicial?.id || camerasPorGravador[0]?.cameras[0]?.id || '');
  const [reproducao, setReproducao] = useState(null); // horario em que a gravacao em tela comecou
  const [posicao, setPosicao] = useState(0); // segundos assistidos desde `reproducao`
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [erro, setErro] = useState('');
  const [gerando, setGerando] = useState(false);
  const [recortes, setRecortes] = useState([]);
  const [maxSegundos, setMaxSegundos] = useState(30 * 60);

  const camera = catalogo.cameras.find((c) => c.id === Number(cameraId)) || null;
  const nomeCamera = camera ? `${gravadoresPorId.get(camera.gravador_id)?.nome} · ${camera.nome}` : '';

  // Horario exato do quadro em tela.
  const horarioAtual = reproducao ? somarSegundos(reproducao, Math.floor(posicao)) : null;

  const assistir = (horario) => {
    setErro('');
    setPosicao(0);
    setReproducao(horario.length === 16 ? `${horario}:00` : horario);
  };

  // Trocar de camera para a gravacao em andamento.
  useEffect(() => { setReproducao(null); }, [cameraId]);

  const carregar = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cameras/recortes`);
      setRecortes(res.data?.data?.recortes || []);
      if (res.data?.data?.max_segundos) setMaxSegundos(res.data.data.max_segundos);
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível carregar os recortes.');
    }
  }, []);

  const processando = recortes.some((r) => r.status === 'processando');
  useEffect(() => {
    carregar();
    const id = setInterval(carregar, processando ? POLL_RAPIDO_MS : POLL_LENTO_MS);
    return () => clearInterval(id);
  }, [carregar, processando]);

  const dInicio = lerLocal(inicio);
  const dFim = lerLocal(fim);
  const duracao = dInicio && dFim ? (dFim - dInicio) / 1000 : NaN;
  const problema = !camera ? 'Escolha a câmera.'
    : !dInicio || !dFim ? 'Marque o início e o fim do recorte.'
      : duracao <= 0 ? 'O fim precisa ser depois do início.'
        : duracao > maxSegundos ? `O recorte pode ter no máximo ${maxSegundos / 60} minutos.`
          : dFim > new Date() ? 'O fim não pode estar no futuro.'
            : '';

  const gerar = async () => {
    if (problema) return;
    setGerando(true);
    setErro('');
    try {
      await axios.post(`${API}/cameras/recortes`, {
        camera_id: camera.id,
        inicio: formatarLocal(dInicio),
        fim: formatarLocal(dFim),
      });
      await carregar();
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível iniciar o recorte.');
    } finally {
      setGerando(false);
    }
  };

  const linkDownload = (id) => {
    const token = localStorage.getItem(TOKEN_KEY) || '';
    return `${API}/cameras/recortes/${id}/download?token=${encodeURIComponent(token)}`;
  };

  return (
    <ModalBase titulo="Gravações — recortar e baixar" onClose={onClose} largura={1080}>
      {erro ? <div className="feedback-banner feedback-banner--error" style={{ marginBottom: 14 }}>{erro}</div> : null}

      <div className="recorte">
        <div className="recorte__player">
          <div className="recorte__tela">
            {camera && reproducao ? (
              <CameraPlayer
                key={`${camera.id}-${reproducao}`}
                camera={camera}
                inicio={reproducao}
                onTempo={setPosicao}
              />
            ) : (
              <div className="cameras-vazio">
                <i className="bi bi-film" style={{ fontSize: 34, opacity: 0.5 }} />
                <span>Escolha a câmera e o horário e clique em “Assistir”.</span>
              </div>
            )}
          </div>

          <div className="recorte__navegacao">
            <span className="recorte__relogio" title="Horário do quadro em tela">
              <i className="bi bi-clock" /> {horarioAtual ? horarioAtual.replace('T', ' ') : '--:--:--'}
            </span>
            {[-60, -10, 10, 60].map((d) => (
              <button
                key={d}
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={!horarioAtual}
                onClick={() => assistir(somarSegundos(horarioAtual, d))}
                title={d < 0 ? `Voltar ${Math.abs(d)} s` : `Avançar ${d} s`}
              >
                {d < 0 ? `− ${Math.abs(d) >= 60 ? '1 min' : `${Math.abs(d)} s`}` : `+ ${d >= 60 ? '1 min' : `${d} s`}`}
              </button>
            ))}
            <button type="button" className="btn btn-primary btn-sm" disabled={!horarioAtual} onClick={() => setInicio(horarioAtual)}>
              <i className="bi bi-skip-start" /> Marcar início
            </button>
            <button type="button" className="btn btn-primary btn-sm" disabled={!horarioAtual} onClick={() => setFim(horarioAtual)}>
              <i className="bi bi-skip-end" /> Marcar fim
            </button>
          </div>
        </div>

        <div className="recorte__lado">
          <label className="portal-field">
            <span>Câmera</span>
            <select value={cameraId} onChange={(e) => setCameraId(e.target.value)}>
              {camerasPorGravador.map(({ gravador, cameras }) => (
                <optgroup key={gravador.id} label={gravador.nome}>
                  {cameras.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </optgroup>
              ))}
            </select>
          </label>

          <CalendarioGravacoes camera={camera} onAssistir={assistir} />

          <hr />

          <label className="portal-field">
            <span>Início do recorte</span>
            <input type="datetime-local" step={1} value={inicio} onChange={(e) => setInicio(e.target.value)} />
          </label>
          <label className="portal-field">
            <span>Fim do recorte</span>
            <input type="datetime-local" step={1} value={fim} onChange={(e) => setFim(e.target.value)} />
          </label>
          <small className="cell-muted">
            Duração: {formatarDuracao(duracao)} (máximo {maxSegundos / 60} min). O servidor copia o trecho do
            equipamento em tempo real: 5 min de vídeo levam cerca de 5 min.
          </small>
          {problema && (inicio || fim) ? <small style={{ color: '#ff8b8b' }}>{problema}</small> : null}
          <button type="button" className="btn btn-primary" disabled={!!problema || gerando} onClick={gerar}>
            <i className="bi bi-scissors me-1" />
            {gerando ? 'Pedindo…' : 'Gerar recorte'}
          </button>
          {camera ? <small className="cell-muted">{nomeCamera}</small> : null}
        </div>
      </div>

      <h3 className="recorte__subtitulo">Recortes recentes</h3>
      <small className="cell-muted">
        Os arquivos ficam disponíveis por 24 horas. A gravação original continua no equipamento.
      </small>
      {recortes.length === 0 ? (
        <div className="empty-state" style={{ marginTop: 10 }}>
          <span>Nenhum recorte ainda.</span>
        </div>
      ) : (
        <div className="table-shell" style={{ marginTop: 10 }}>
          <table className="portal-table">
            <thead>
              <tr>
                <th>Câmera</th>
                <th>Período</th>
                <th>Pedido por</th>
                <th>Situação</th>
                <th style={{ width: 120 }} />
              </tr>
            </thead>
            <tbody>
              {recortes.map((r) => (
                <tr key={r.id}>
                  <td className="cell-strong">{r.camera_nome}</td>
                  <td>{formatarPeriodo(r.inicio, r.fim)}</td>
                  <td>{r.usuario || '—'}</td>
                  <td>
                    {r.status === 'processando' && (
                      <div className="recorte__progresso" title={`${r.progresso}%`}>
                        <div style={{ width: `${Math.max(3, r.progresso)}%` }} />
                        <span>Copiando do equipamento… {r.progresso}%</span>
                      </div>
                    )}
                    {r.status === 'pronto' && <span className="permissao-chip permissao-chip--on">Pronto · {formatarTamanho(r.tamanho)}</span>}
                    {r.status === 'expirado' && <span className="permissao-chip">Expirado</span>}
                    {r.status === 'erro' && <small style={{ color: '#ff8b8b' }}>{r.erro || 'Falhou'}</small>}
                  </td>
                  <td>
                    {r.status === 'pronto' && (
                      <a className="btn btn-primary btn-sm" href={linkDownload(r.id)}>
                        <i className="bi bi-download" /> Baixar
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ModalBase>
  );
}
